import { useState, useId } from "react";
import toast from "react-hot-toast";
import { getFileNameFromDownloadResponse, isSafeUrl } from "../../utils/util";
import dummyProjectImage from "../../assets/Projects/project1.png";

const DUMMY_IMAGE_FALLBACK = dummyProjectImage;

const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="30"
    viewBox="0 0 24 30"
    className="text-white"
  >
    {/* File body → white → pink */}
    <path
      d="M3 2h12l6 6v18a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
      fill="currentColor"
      className="transition-colors duration-300 group-hover:text-[#E761A0]"
    />

    {/* Fold */}
    <path
      d="M15 2v6h6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="transition-colors duration-300 group-hover:text-[#E761A0]"
    />

    {/* Arrow group */}
    <g
      className="
      text-[#E761A0]
      transition-all duration-300 ease-out
      group-hover:translate-y-4
      group-hover:opacity-0
      group-hover:text-white
    "
    >
      <line
        x1="12"
        y1="11"
        x2="12"
        y2="17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <polyline
        points="9,14 12,17 15,14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>

    {/* Bottom line */}
    <line
      x1="7"
      y1="22"
      x2="17"
      y2="22"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="text-[#E761A0] group-hover:text-white transition-colors duration-300"
    />
  </svg>
);

const TrackProgress = () => {
  const clipId = useId();
  return (
    <svg
    className="w-7 transition-transform duration-500 ease-in-out group-hover:rotate-180"
    id="Group_59"
    data-name="Group 59"
    xmlns="http://www.w3.org/2000/svg"
    width="27.164"
    height="27.154"
    viewBox="0 0 27.164 27.154"
  >
    <defs>
      <clipPath id={clipId}>
        <rect
          id="Rectangle_65"
          data-name="Rectangle 65"
          width="27.164"
          height="27.154"
          fill="#fff"
        />
      </clipPath>
    </defs>
    <g id="Group_58" data-name="Group 58" clipPath={`url(#${clipId})`}>
      <path
        id="Path_201"
        data-name="Path 201"
        d="M475.85,8.9l-5.837,1.9a7.459,7.459,0,0,0-6.423-4.662V0a13.589,13.589,0,0,1,12.26,8.9"
        transform="translate(-449.509)"
        fill="#fff"
      />
      <path
        id="Path_202"
        data-name="Path 202"
        d="M39.39,0V6.137A7.461,7.461,0,0,0,32.966,10.8L27.13,8.9A13.589,13.589,0,0,1,39.39,0"
        transform="translate(-26.306)"
        fill="#fff"
      />
      <path
        id="Path_203"
        data-name="Path 203"
        d="M613.3,328.051a13.558,13.558,0,0,1-5.2,10.689l-3.6-4.959a7.468,7.468,0,0,0,2.452-7.555l5.836-1.9a13.589,13.589,0,0,1,.516,3.721"
        transform="translate(-586.14 -314.479)"
        fill="#fff"
      />
      <path
        id="Path_204"
        data-name="Path 204"
        d="M8.8,333.771,5.2,338.73A13.6,13.6,0,0,1,.516,324.32l5.836,1.9A7.467,7.467,0,0,0,8.8,333.771"
        transform="translate(0 -314.469)"
        fill="#fff"
      />
      <path
        id="Path_205"
        data-name="Path 205"
        d="M212.911,659.749a13.589,13.589,0,0,1-15.152,0l3.6-4.958a7.461,7.461,0,0,0,7.946,0Z"
        transform="translate(-191.753 -634.902)"
        fill="#fff"
      />
    </g>
    </svg>
  );
};

const UploadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-white"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export interface ReportItem {
  id: string;
  title: string;
  description: string;
  completedDate: string;
  image: string;
  /** URL of the report file for download (admin type 0). */
  reportFileUrl?: string;
}

interface ReportListItemProps {
  report: ReportItem;
  isLast?: boolean;
  /** User type: "0" = admin (download only), "2" = user (upload only). */
  userType?: string;
  onDownload?: (report: ReportItem) => void;
  onUpload?: (report: ReportItem, file: File) => void;
  /** Optional icon image src for the download button (e.g. track-progress.svg). Defaults to the document download icon. */
  downloadButtonIcon?: string;
  /** Optional button label for download. Defaults to "DOWNLOAD REPORT". */
  buttonLabel?: string;
  /** Optional label for upload button. Defaults to "UPLOAD REPORT". */
  uploadButtonLabel?: string;
  /** When true, show loading state on the upload button (e.g. while uploading). */
  uploadLoading?: boolean;
  /** Label for the date field (e.g. "Completed" or "Created At"). Defaults to "Completed". */
  dateLabel?: string;
  /** When true and userType is "0", the main button calls onDownload (e.g. navigate) instead of file download. Used on Track Audits for "Update progress". */
  useNavigateForAdmin?: boolean;
  /** When true and userType is "2", show "Download report" button (functional download) instead of upload. Used on Reports page. */
  showDownloadForUser?: boolean;
  /** When true and userType is "2", show Track progress button that calls onDownload (e.g. navigate to /ifc-processing/:id). Used on Track Audits. */
  useNavigateForUserType2?: boolean;
}

const ReportListItem = ({
  report,
  isLast = false,
  userType,
  onDownload,
  onUpload,
  downloadButtonIcon,
  buttonLabel = "DOWNLOAD REPORT",
  uploadButtonLabel = "UPLOAD REPORT",
  uploadLoading = false,
  dateLabel = "Completed",
  useNavigateForAdmin = false,
  showDownloadForUser = false,
  useNavigateForUserType2 = false,
}: ReportListItemProps) => {
  const isUploadMode =
    userType === "2" && !showDownloadForUser && !useNavigateForUserType2;
  const isDownloadMode = userType === "0";
  const reportFileUrl = report.reportFileUrl;
  const canDownload =
    (isDownloadMode && reportFileUrl) ||
    (showDownloadForUser && userType === "2" && reportFileUrl);
  const isGenericButton = userType !== "0" && userType !== "2";
  const [downloadLoading, setDownloadLoading] = useState(false);
  const isNavigateButton = isDownloadMode && useNavigateForAdmin && onDownload;
  const isNavigateForUser2 =
    userType === "2" && useNavigateForUserType2 && onDownload;

  const handleDownload = async () => {
    if (!reportFileUrl || !isSafeUrl(reportFileUrl)) return;
    setDownloadLoading(true);
    try {
      const response = await fetch(reportFileUrl, { mode: "cors" });
      if (!response.ok) throw new Error("Download failed");
      const fileName = getFileNameFromDownloadResponse(response, reportFileUrl, "report");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      onDownload?.(report);
      toast.success("Report downloaded.");
    } catch (err) {
      try {
        const link = document.createElement("a");
        link.href = reportFileUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Opening report.");
      } catch (fallbackErr) {
        toast.error("Failed to download report.");
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleUploadClick = () => {
    if (uploadLoading) return;
    const input = document.getElementById(
      `report-upload-${report.id}`,
    ) as HTMLInputElement | null;
    input?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(report, file);
    }
    e.target.value = "";
  };

  const buttonClass =
    "text-[14px] xl:text-[16px] font-weight-600 group overflow-hidden flex items-center gap-2 px-5 py-[6px] rounded-[27px] border-2 border-[#E761A0] bg-[#E761A0] text-white transition-all duration-300 ease-out hover:bg-transparent btn-hover-color min-h-[54px] text-uppercase disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <div
      className={`flex flex-col gap-5 md:flex-row flex-wrap items-center py-7 lg:py-6 xl:py-8 ${!isLast ? "border-b border-white/20" : ""
        }`}
    >
      <div className="w-full h-[200px] md:w-[161px] xl:w-[181px] md:h-[107px] shrink-0 overflow-hidden rounded-[12px] bg-gray-700/50">
        {report?.image ? <img
          src={report.image}
          alt={report.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = DUMMY_IMAGE_FALLBACK;
          }} /> :
          <img
            src={DUMMY_IMAGE_FALLBACK}
            alt=""
            className="w-full h-full object-cover"
          />}
      </div>
      <div className="description-wrap max-w-[400px] text-center md:text-left  md:ml-[25px] lg:ml-[20px] xl:ml-[50px]">
        <h3 className="text-[18px] font-weight-600 text-white leading-tight mb-3">
          {report.title}
        </h3>
        <p className="text-[14px] lg:text-[16px] xl:text-[18px] font-weight-300 text-white leading-snug line-clamp-2 ">
          {report.description}
        </p>
      </div>

      <div className="flex-0 flex flex-col lg:justify-end min-w-0 max-w-[350px] xl:max-w-[420px] md:ml-auto lg:mt-0 mt-3">
        <p className="text-[14px] lg:text-[16px] xl:text-[18px] font-weight-300 text-white leading-snug line-clamp-2  mb-3 text-center lg:text-right">
          {dateLabel}: {report.completedDate || "N/A"}
        </p>

        {isUploadMode ? (
          <>
            <input
              id={`report-upload-${report.id}`}
              type="file"
              className="hidden"
              accept=".xlsx,.xls,.csv,.pdf,.doc,.docx,.zip"
              onChange={handleFileChange}
            />
            <button
              type="button"
              className={buttonClass}
              onClick={handleUploadClick}
              disabled={uploadLoading}
            >
              {uploadLoading ? (
                <span className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
              ) : (
                <UploadIcon />
              )}
              {uploadLoading ? "Uploading..." : uploadButtonLabel}
            </button>
          </>
        ) : isGenericButton ? (
          <button
            type="button"
            className={buttonClass}
            onClick={() => onDownload?.(report)}
          >
            {downloadButtonIcon ? <TrackProgress /> : <DownloadIcon />}
            {buttonLabel}
          </button>
        ) : isNavigateForUser2 ? (
          <button
            type="button"
            className={buttonClass}
            onClick={() => onDownload?.(report)}
          >
            {downloadButtonIcon ? <TrackProgress /> : <DownloadIcon />}
            {buttonLabel}
          </button>
        ) : isNavigateButton ? (
          <button
            type="button"
            className={buttonClass}
            onClick={() => onDownload?.(report)}
          >
            {downloadButtonIcon ? <TrackProgress /> : <DownloadIcon />}
            {buttonLabel}
          </button>
        ) : (
          <button
            type="button"
            className={buttonClass}
            onClick={handleDownload}
            disabled={!canDownload || downloadLoading}
          >
            {downloadLoading ? (
              <span className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
            ) : downloadButtonIcon && !showDownloadForUser ? (
              <TrackProgress />
            ) : (
              <DownloadIcon />
            )}
            {downloadLoading
              ? "Downloading..."
              : showDownloadForUser && userType === "2"
                ? "Download report"
                : buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default ReportListItem;
